import noop from 'lodash/noop';
import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { sleep } from '../../utils/helpers';
import { devLog } from '../../utils/log';
import { type CoinType } from '../cointype/CoinTypeStore';
import type { RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';

import {
  type FeatureFetchStatus,
  type FeatureManagerData,
  type FeatureManagerDialogType,
  type FeatureManagerSequenceState,
  type FeatureManagerStatus,
  type IFeatureManagerStore,
} from './types';
import { fetchOrder } from './utils';

export class FeatureManagerStore extends Loadable {
  private rootStore: RootStore;
  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);

    const preventPopupAutoDisplayFlag = resolveFlag('prevent_popup_auto_display_web', false);
    this.setPreventPopupAutoDisplayFlag(preventPopupAutoDisplayFlag);

    this.setExcludedCoinTypesByDialog(
      resolveFlag<Partial<Record<FeatureManagerDialogType, CoinType[]>>>('feature_manager_excluded_coin_types', {}),
    );

    ldClient?.on('ready', () => {
      this.setPreventPopupAutoDisplayFlag(resolveFlag('prevent_popup_auto_display_web', false));
      this.setExcludedCoinTypesByDialog(
        resolveFlag<Partial<Record<FeatureManagerDialogType, CoinType[]>>>('feature_manager_excluded_coin_types', {}),
      );
    });

    ldClient?.on('change', (changes) => {
      if ('prevent_popup_auto_display_web' in changes) {
        this.setPreventPopupAutoDisplayFlag(resolveFlag('prevent_popup_auto_display_web', false));
      }
      if ('feature_manager_excluded_coin_types' in changes) {
        this.setExcludedCoinTypesByDialog(
          resolveFlag<Partial<Record<FeatureManagerDialogType, CoinType[]>>>('feature_manager_excluded_coin_types', {}),
        );
      }
    });

    this.setStores([
      this.rootStore.forceKycStore,
      this.rootStore.pocStore,
      this.rootStore.dailyMissionsStore,
      this.rootStore.missionsStore,
      this.rootStore.eventTriggerStore,
      this.rootStore.promotionStore,
      this.rootStore.tournamentStore,
      this.rootStore.loyaltyProgramStore,
      this.rootStore.rewardsStore,
      this.rootStore.termsOfServiceStore,
      this.rootStore.coinBackStore,
      this.rootStore.payRedeemStore,
      this.rootStore.userFreeSpinsStore,
      this.rootStore.lowBalanceStore,
      this.rootStore.onboardingStore,
      this.rootStore.bingoStore,
      this.rootStore.questStore,
      this.rootStore.stampCardStore,
      this.rootStore.metagamesStore,
      this.rootStore.marketingConsentStore,
      this.rootStore.userSnappyGiftsStore,
      this.rootStore.challengesStore,
      this.rootStore.monetizationEventsStore,
      this.rootStore.sofStore,
      this.rootStore.actionEventTriggerStore,
      this.rootStore.widgetsStore,
      this.rootStore.cashOfferStore,
    ]);

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        void this.initializeFeatureManager();
      }
    });
  }

  private readonly MAX_WAITING_TIME = 4000;

  private preventPopupAutoDisplayFlag = false;

  private setPreventPopupAutoDisplayFlag(value: boolean) {
    this.preventPopupAutoDisplayFlag = value;
  }

  private excludedCoinTypesByDialog: Partial<Record<FeatureManagerDialogType, CoinType[]>> = {};

  private setExcludedCoinTypesByDialog(value: Partial<Record<FeatureManagerDialogType, CoinType[]>>) {
    this.excludedCoinTypesByDialog = value;
  }

  @observable
  public sequenceQueueInitialized = false;
  @action
  public setSequenceQueueInitialized(value: boolean) {
    this.sequenceQueueInitialized = value;
  }

  private timeOut: NodeJS.Timeout | null = null;
  public startMainSequence() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
    }
    this.setSequenceState('PRE_START');
    this.setSequenceQueueInitialized(false);
    this.timeOut = setTimeout(() => {
      this.initializeSequenceQueue();
      this.setSequenceQueueInitialized(true);
    }, 1000);
  }

  private fetchingTimeoutPassed = false;

  @computed
  public get readyForFeaturesSequence() {
    const allFeaturesReady = Object.values(this.featuresStatuses || {}).every((status) => status !== 'FETCHING');
    return this.sequenceQueueInitialized && (allFeaturesReady || this.fetchingTimeoutPassed);
  }

  private eventsAwaitedOnce = false;

  private eventsSettled: Promise<void> | null = null;

  private isFirstFetch = true;

  public setIsFirstFetch(value: boolean) {
    this.isFirstFetch = value;
  }

  // Use to detect coin type switch
  @observable
  public currentCoinType: CoinType = 'GC';
  @action
  public setCoinType(coinType: CoinType) {
    this.currentCoinType = coinType;
  }

  private stores: IFeatureManagerStore[] = [];

  private setStores(newStores: IFeatureManagerStore[]) {
    this.stores = newStores;
  }

  @observable
  public sequenceState: FeatureManagerSequenceState = 'OFF';

  @action
  public setSequenceState(state: FeatureManagerSequenceState) {
    this.sequenceState = state;
  }

  @observable
  public featuresStatuses: Record<FeatureManagerDialogType, FeatureManagerStatus> | null = null;
  @action
  public setFeaturesStatuses(featuresStatuses: Record<FeatureManagerDialogType, FeatureManagerStatus> | null) {
    this.featuresStatuses = featuresStatuses;
    devLog('setFeaturesStatuses featuresStatuses', toJS(featuresStatuses));
  }

  @action
  public initializeFeaturesStatuses(args: { isCoinChange?: boolean }) {
    const featuresStatuses: Record<FeatureManagerDialogType, FeatureManagerStatus> = {
      ...this.featuresStatuses,
    } as Record<FeatureManagerDialogType, FeatureManagerStatus>;
    for (const dialogType in this.dialogManagerData) {
      if (args.isCoinChange && !this.refetchOnCoinChangeTypes.includes(dialogType as FeatureManagerDialogType)) {
        continue;
      }
      featuresStatuses[dialogType as FeatureManagerDialogType] = 'FETCHING';
    }
    this.setFeaturesStatuses(featuresStatuses);
  }

  public async startDialogs(dialogType?: FeatureManagerDialogType | null) {
    if (!dialogType) {
      return false;
    }

    if (this.preventPopupAutoDisplayFlag) {
      devLog('FeatureManagerStore: startDialogs preventPopupAutoDisplayFlag is true, skipping dialog start');
      return false;
    }

    const { coinType } = this.rootStore.coinTypeStore;
    const excludedCoinTypes = this.excludedCoinTypesByDialog[dialogType];
    if (Array.isArray(excludedCoinTypes) && excludedCoinTypes.includes(coinType)) {
      devLog('FeatureManagerStore: startDialogs dialogType excluded for coinType, skipping dialog start', {
        dialogType,
        coinType,
      });
      return false;
    }

    const foundStore = this.stores.find((store) => store.dialogTypes.includes(dialogType));

    if (!foundStore) {
      return false;
    }

    return foundStore.startDialogs(dialogType, this.isFirstFetch ? 'after_login' : 'back_to_lobby');
  }

  // Per-lobby auto-open switch from the order config (cc → GC coin type, sc → SC). Absent → on (default enabled).
  public isDialogLobbyEnabled(dialogType: FeatureManagerDialogType, coinType: CoinType): boolean {
    const item = this.dialogManagerData?.[dialogType];
    const key = coinType === 'GC' ? 'cc_enabled' : 'sc_enabled';
    return item?.[key] ?? true;
  }

  private getCurrentStore() {
    const currentDialog = this.currentFeatureType;
    if (currentDialog) {
      return this.stores.find((f) => f.dialogTypes.includes(currentDialog));
    }
    return null;
  }

  private delayBetweenTimeout: NodeJS.Timeout | null = null;

  public onDialogFinished(featureType: FeatureManagerDialogType) {
    const currentStore = this.getCurrentStore();
    if (this.sequenceState === 'FEATURE_IN_PROGRESS' && currentStore?.dialogTypes.includes(featureType)) {
      if (this.delayBetweenTimeout) {
        clearTimeout(this.delayBetweenTimeout);
      }
      this.delayBetweenTimeout = setTimeout(() => {
        this.setSequenceState('CHECK_QUEUE_STATE');
      }, 300);
    }
  }

  public async fetchStores({ isCoinChange }: { isCoinChange?: boolean }) {
    const { coinType } = this.rootStore.coinTypeStore;

    if (this.loadingByCoinType[coinType]) {
      return;
    }

    this.setLoadingByCoinType(coinType, true);

    // try/finally, not straight-line returns: `loadingByCoinType` is the re-entrancy guard read
    // at the top of this method, so leaving it set latches the whole dialog pipeline off for the
    // rest of the page's life — every feature store silently stops refetching. Same for
    // `eventsSettled`, which would stall missionsStore for MAX_WAITING_TIME on every later run.
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let resolveEvents: () => void = noop;
    // The guard is per coinType, so a GC run can overlap an SC run: only clear what we armed.
    let armedEvents = false;
    try {
      if (!this.dialogsOrder) {
        // Lost the race against the config request; the caller retries and finally releases.
        devLog('FeatureManagerStore: fetchStores called before dialogsOrder was ready');
        return;
      }
      this.initializeFeaturesStatuses({ isCoinChange });
      if (!this.featuresStatuses) {
        return;
      }

      const eventsDialogTypes = this.rootStore.eventTriggerStore.dialogTypes;
      const eventsWillFetch =
        !isCoinChange &&
        !this.eventsAwaitedOnce &&
        this.dialogsOrder.some((featureType) => eventsDialogTypes.includes(featureType.type));

      if (eventsWillFetch) {
        this.eventsSettled = new Promise<void>((resolve) => {
          resolveEvents = resolve;
        });
        this.eventsAwaitedOnce = true;
        armedEvents = true;
      }

      this.fetchingTimeoutPassed = false;

      timeout = setTimeout(() => {
        this.fetchingTimeoutPassed = true;
      }, this.MAX_WAITING_TIME);

      const nextStatuses = { ...this.featuresStatuses };
      await Promise.allSettled(
        this.dialogsOrder.map(async (featureType) => {
          if (isCoinChange && !this.refetchOnCoinChangeTypes.includes(featureType.type)) {
            return;
          }
          const foundStore = this.stores.find((store) => store.dialogTypes.includes(featureType.type));
          if (foundStore) {
            const fetchType = this.isFirstFetch ? 'INTIAL' : isCoinChange ? 'COIN_TYPE_CHANGE' : 'BACK_TO_LOBBY';
            if (this.eventsSettled && foundStore === this.rootStore.missionsStore) {
              await Promise.race([this.eventsSettled, sleep(this.MAX_WAITING_TIME)]);
            }
            const isEventsStore = eventsWillFetch && foundStore === this.rootStore.eventTriggerStore;
            let fetchResult: FeatureFetchStatus;
            try {
              fetchResult = await foundStore.fetch({ fetchType });
            } finally {
              if (isEventsStore) {
                resolveEvents();
                this.eventsSettled = null;
              }
            }
            if (fetchResult !== 'FETCHING') {
              foundStore.dialogTypes.forEach((dialogType) => {
                if (nextStatuses[dialogType] === 'FETCHING') {
                  nextStatuses[dialogType] = fetchResult;
                }
              });
            }
          } else {
            devLog('Store not found', { featureType });
            nextStatuses[featureType.type] = 'OFF';
          }
          this.setFeaturesStatuses(nextStatuses);
        }),
      );
    } finally {
      if (armedEvents) {
        resolveEvents();
        this.eventsSettled = null;
      }
      if (timeout) {
        clearTimeout(timeout);
      }
      this.setLoadingByCoinType(coinType, false);
      this.fetchingTimeoutPassed = true;
    }
  }

  private refetchOnCoinChangeTypes: FeatureManagerDialogType[] = [];
  public setRefetchOnCoinChangeTypes(data: FeatureManagerData) {
    this.refetchOnCoinChangeTypes = Object.keys(data).reduce((acc, key) => {
      const dialogTypeConfig = data[key as FeatureManagerDialogType];
      if (dialogTypeConfig?.refetchOnCoinChange) {
        const foundStore = this.stores.find((store) => store.dialogTypes.includes(key as FeatureManagerDialogType));
        foundStore?.dialogTypes.forEach((dialogType) => {
          acc.push(dialogType);
        });
      }
      return acc;
    }, [] as FeatureManagerDialogType[]);
  }

  @observable
  public storeStatus: FeatureManagerStatus = 'OFF';
  @action
  public setStoreStatus(status: FeatureManagerStatus) {
    this.storeStatus = status;
  }

  public dialogManagerData: FeatureManagerData | null = null;
  public setDialogManagerData(dmData: FeatureManagerData) {
    this.dialogManagerData = dmData;
  }

  @observable
  public dialogsOrder: Array<{ type: FeatureManagerDialogType; order: number }> | null = null;
  @action
  public initializeOrderStructure(dialogManagerData: FeatureManagerData) {
    const nextOrder = Object.keys(dialogManagerData)
      .map((key) => ({
        type: key as FeatureManagerDialogType,
        order: dialogManagerData[key as FeatureManagerDialogType].order,
      }))
      .sort((a, b) => a.order - b.order);
    this.dialogsOrder = nextOrder;
  }

  @observable
  public sequenceQueue: FeatureManagerDialogType[] = [];
  @action
  public initializeSequenceQueue() {
    if (this.dialogManagerData) {
      const newSequenceQueue = [
        ...new Set([
          ...this.sequenceQueue,
          ...Object.keys(this.dialogManagerData).map((key) => key as FeatureManagerDialogType),
        ]),
      ].sort((a, b) => {
        const indexA = this.dialogsOrder?.findIndex((order) => order.type === a) || 0;
        const indexB = this.dialogsOrder?.findIndex((order) => order.type === b) || 0;
        return indexA - indexB;
      });

      this.sequenceQueue = newSequenceQueue;
    }
  }

  @action
  public addDialogsToSequenceQueue(dialogTypes: FeatureManagerDialogType[], shouldStartSequence = false) {
    devLog('addDialogsToSequenceQueue, dialog types:', dialogTypes);
    const nextSequenceQueue = [...this.sequenceQueue];

    for (const dialogType of dialogTypes) {
      if (!nextSequenceQueue.includes(dialogType)) {
        nextSequenceQueue.push(dialogType);
      }
    }
    this.sequenceQueue = nextSequenceQueue;

    if (shouldStartSequence) {
      this.setSequenceState('CHECK_QUEUE_STATE');
    }
  }

  @computed
  public get isQueueEmpty() {
    devLog('sequenceQueue', toJS(this.sequenceQueue));
    return this.sequenceQueue.length === 0;
  }

  @observable
  public currentFeatureType: FeatureManagerDialogType | null = null;
  @action
  public setCurrentFeatureType(dialogType: FeatureManagerDialogType | null) {
    this.currentFeatureType = dialogType;
  }

  @action
  public resetSequenceQueue() {
    this.setSequenceState('OFF');
    this.sequenceQueue = [];
  }

  public getCurrentFeatureType() {
    return this.currentFeatureType;
  }

  public getNextFeatureType() {
    return this.sequenceQueue[0];
  }

  @action
  public shiftFeatureType() {
    const nextSequenceQueue = [...this.sequenceQueue];
    const nextFeatureType = nextSequenceQueue.shift();
    this.setCurrentFeatureType(nextFeatureType || null);
    this.sequenceQueue = nextSequenceQueue;
    return nextFeatureType;
  }

  public getFeatureStatus(featureType?: FeatureManagerDialogType) {
    const dialogTypeToCheck = featureType || this.currentFeatureType;
    return dialogTypeToCheck ? this.featuresStatuses?.[dialogTypeToCheck] : 'OFF';
  }

  @action
  public updateFeatureStatus(featureType: FeatureManagerDialogType, status: FeatureManagerStatus) {
    if (!this.featuresStatuses) return;
    this.setFeaturesStatuses({ ...this.featuresStatuses, [featureType]: status });
  }

  public async initializeFeatureManager() {
    try {
      this.setStoreStatus('FETCHING');

      const featureManagerOrderConfigurations = await fetchOrder();
      this.addFeatureConfigurationToQueueEnd(featureManagerOrderConfigurations);

      if (featureManagerOrderConfigurations) {
        this.setDialogManagerData(featureManagerOrderConfigurations);
        this.initializeOrderStructure(featureManagerOrderConfigurations);
        this.setRefetchOnCoinChangeTypes(featureManagerOrderConfigurations);
        this.setStoreStatus('READY');
        return;
      }
      this.setStoreStatus('ERROR');
    } catch (e) {
      devLog('FeatureManager initializeOrder ERROR', e);
      this.setStoreStatus('ERROR');
    }
  }

  public async fetchAndStartDialogs(featureTypes: FeatureManagerDialogType[], options?: { force?: boolean }) {
    await Promise.allSettled(
      featureTypes.map(async (featureType) => {
        const foundStore = this.stores.find((store) => store.dialogTypes.includes(featureType));
        if (foundStore) {
          const fetchResult = await foundStore.fetch({ fetchType: 'MANUAL' }, options);

          if (fetchResult !== 'FETCHING') {
            const isInFeaturesStatuses = this.featuresStatuses && this.featuresStatuses[featureType];

            if (this.featuresStatuses && isInFeaturesStatuses) {
              this.setFeaturesStatuses({
                ...this.featuresStatuses,
                [featureType]: fetchResult,
              });
            }

            if (fetchResult === 'READY') {
              this.addDialogsToSequenceQueue([featureType]);
            }
          }
        }
      }),
    );
  }

  private addFeatureConfigurationToQueueEnd(featureManagerOrderConfigurations: FeatureManagerData) {
    if (featureManagerOrderConfigurations) {
      featureManagerOrderConfigurations['EventTriggerActionCoinTutorial'] = {
        order: Infinity,
      };
    }
  }
}
